import React from "react";
import { ArrowRight } from "lucide-react";
import { useModal } from "../context/ModalContext";
import useScrollAnimation from "../hooks/useScrollAnimation";
import { DottedSurface } from "./ui/dotted-surface";

const CaseStudyPage = ({
  category,
  client,
  title,
  heroImage,
  challenge,
  whatWeBuilt,
  results,
  takeaway,
}) => {
  const { openDemoModal } = useModal();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.08 });

  return (
    <section className="relative py-24 md:py-28 bg-[--bg-primary] min-h-[70vh]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">

        {/* Hero */}
        <div
          className={`relative overflow-hidden soft-card rounded-3xl transition-all duration-700 ease-out ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          {heroImage && (
            <img
              src={heroImage}
              alt={title}
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading="eager"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
          <DottedSurface className="opacity-20 [mask-image:radial-gradient(circle_at_76%_24%,black_15%,transparent_75%)]" />

          <div className="relative z-10 p-8 md:p-10 lg:p-14 min-h-[420px] flex flex-col justify-end">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-white/80">
                {category}
              </span>
              <span className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-white/80">
                Case Study
              </span>
            </div>
            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/60 mb-3">
              {client}
            </p>
            <h1 className="text-[clamp(1.8rem,3.8vw,3.4rem)] font-semibold text-white leading-[1.06] max-w-4xl">
              {title}
            </h1>
          </div>
        </div>

        {/* Body */}
        <div className="mt-10 grid lg:grid-cols-[1fr_380px] gap-8 lg:gap-10 items-start">

          {/* Main content */}
          <div className="space-y-8 order-2 lg:order-1">

            {/* Challenge */}
            <div
              className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "120ms" }}
            >
              <p className="section-eyebrow mb-4">The Challenge</p>
              <p className="text-[--text-secondary] leading-relaxed text-[1.05rem]">{challenge}</p>
            </div>

            {/* What We Built */}
            <div
              className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              <p className="section-eyebrow mb-4">What We Built</p>
              <p className="text-[--text-secondary] leading-relaxed text-[1.05rem]">{whatWeBuilt}</p>
            </div>

            {/* Takeaway */}
            <div
              className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "280ms" }}
            >
              <p className="section-eyebrow mb-4">Takeaway</p>
              <p className="text-[--text-secondary] leading-relaxed text-[1.05rem] italic">{takeaway}</p>
            </div>
          </div>

          {/* Results sidebar */}
          <div
            className={`soft-card rounded-2xl p-7 lg:p-8 order-1 lg:order-2 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "160ms" }}
          >
            <p className="section-eyebrow mb-6">Results</p>
            <div className="space-y-6">
              {results.map((result, i) => (
                <div key={i} className="border-b border-[--border] pb-6 last:border-0 last:pb-0">
                  <p className="text-2xl font-semibold text-[--accent] leading-none">{result.value}</p>
                  <p className="mt-2 text-sm text-[--text-secondary] leading-relaxed">{result.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10">
          <div
            className={`relative overflow-hidden soft-card rounded-3xl p-8 md:p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "360ms" }}
          >
            <DottedSurface className="opacity-45 [mask-image:radial-gradient(circle_at_70%_45%,black_14%,transparent_76%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_28%,var(--accent-glow)_0%,transparent_48%)]" />

            <div className="relative z-10 max-w-2xl">
              <p className="section-eyebrow mb-4">Work With Us</p>
              <h2 className="text-[clamp(1.7rem,3.2vw,3rem)] font-semibold text-[--text-primary] leading-[1.06]">
                Want results like these?
                <br />
                <span className="accent-wash">Start with a free audit.</span>
              </h2>
              <p className="mt-5 text-[--text-secondary] text-lg leading-relaxed">
                We'll map out where AI can create measurable lift in your operations — no fluff, no sales deck.
              </p>
            </div>

            <div className="relative z-10">
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all inline-flex items-center gap-2 btn-pattern"
              >
                Book a Free Audit
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CaseStudyPage;
